import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { flyers as flyersApi } from '../utils/api';
import { 
  Search, 
  Filter, 
  FileImage, 
  Tag,
  X,
  Grid3X3,
  List
} from 'lucide-react';

const FlyerLibrary = () => {
  const [flyersList, setFlyersList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await flyersApi.getCategories();
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const debounce = setTimeout(() => {
      setLoading(true);
      flyersApi.getAll({
        search: searchTerm,
        category: selectedCategory
      }, { signal: controller.signal })
        .then(response => setFlyersList(response.data.flyers))
        .catch(error => {
          if (error.code !== 'ERR_CANCELED') console.error('Error searching flyers:', error);
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
    }, 300);
    return () => {
      clearTimeout(debounce);
      controller.abort();
    };
  }, [searchTerm, selectedCategory]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
  };

  if (loading && flyersList.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 font-display">Flyer Library</h1>
        <p className="text-gray-600 mt-2">
          Browse and download marketing flyers with your branding
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search Input */}
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search flyers by title or tags..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-12"
            />
          </div>

          {/* Category Filter */}
          <div className="relative min-w-[200px]">
            <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="input-field pl-11 appearance-none cursor-pointer"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.slug}>
                  {cat.name} ({cat.flyerCount})
                </option>
              ))}
            </select>
          </div>

          {/* View Toggle */}
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid' 
                  ? 'bg-white shadow-sm text-primary-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Grid3X3 size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white shadow-sm text-primary-500' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <List size={20} />
            </button>
          </div>
        </div>

        {/* Active Filters */}
        {(searchTerm || selectedCategory) && (
          <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
            <span className="text-sm text-gray-500">Active filters:</span>
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                "{searchTerm}"
                <button onClick={() => setSearchTerm('')} className="hover:text-primary-900">
                  <X size={14} />
                </button>
              </span>
            )}
            {selectedCategory && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-sm">
                {categories.find(c => c.slug === selectedCategory)?.name}
                <button onClick={() => setSelectedCategory('')} className="hover:text-secondary-900">
                  <X size={14} />
                </button>
              </span>
            )}
            <button 
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 ml-2"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="w-6 h-6 border-3 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
        </div>
      ) : flyersList.length === 0 ? (
        <div className="text-center py-16">
          <FileImage className="mx-auto text-gray-300 mb-4" size={64} />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No flyers found</h3>
          <p className="text-gray-500">Try adjusting your search or filters</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-4">
            Showing {flyersList.length} flyer{flyersList.length !== 1 ? 's' : ''}
          </p>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {flyersList.map((flyer) => (
                <Link key={flyer.id} to={`/flyers/${flyer.id}`} className="flyer-card">
                  <div className="aspect-[3/4] bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center relative overflow-hidden">
                    {flyer.thumbnailUrl ? (
                      <img 
                        src={flyer.thumbnailUrl} 
                        alt={flyer.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-4">
                        <FileImage className="mx-auto text-gray-300 mb-2" size={48} />
                        <p className="text-xs text-gray-400">Preview</p>
                      </div>
                    )}
                    {flyer.category && (
                      <span className="absolute top-3 left-3 px-2 py-1 bg-white/90 backdrop-blur-sm rounded-md text-xs font-medium text-gray-700">
                        {flyer.category}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-800 truncate">{flyer.title}</h3>
                    {flyer.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{flyer.description}</p>
                    )}
                    {flyer.tags && flyer.tags.length > 0 && (
                      <div className="flex items-center gap-1 mt-2 flex-wrap">
                        <Tag size={12} className="text-gray-400" />
                        {flyer.tags.slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="text-xs text-gray-500">
                            {tag}{idx < Math.min(flyer.tags.length - 1, 2) ? ',' : ''}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {flyersList.map((flyer) => (
                <Link 
                  key={flyer.id} 
                  to={`/flyers/${flyer.id}`} 
                  className="card p-4 flex items-center gap-4 hover:border-primary-300"
                >
                  <div className="w-20 h-28 bg-gradient-to-br from-gray-100 to-gray-50 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {flyer.thumbnailUrl ? (
                      <img 
                        src={flyer.thumbnailUrl} 
                        alt={flyer.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <FileImage className="text-gray-300" size={32} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-800">{flyer.title}</h3>
                    {flyer.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-1">{flyer.description}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2">
                      {flyer.category && (
                        <span className="text-xs px-2 py-1 bg-gray-100 rounded-md text-gray-600">
                          {flyer.category}
                        </span>
                      )}
                      {flyer.tags && flyer.tags.slice(0, 3).map((tag, idx) => (
                        <span key={idx} className="text-xs text-gray-400">#{tag}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FlyerLibrary;
